import { css, html, nothing, PropertyValueMap } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import { PassValidationCompStatus, spinner } from '../../../common/constants/api-constants';
import store from '../../../store/store';
import { StoreState } from '../../../store/store.interface';
import { updateUserProfile } from '../../user-profile/thunks/update-user-profile.thunk';
import { UserAffiliation, UserProfile } from '../../user-profile/user-profile.interface';
import { MyAccountFormWrapper } from '../my-account-form-wrapper';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import environment from '../../../common/environment';
import { validateUniqueEmail } from '../../user-profile/thunks/validate-unique-user.thunk';
import {
  getDepartmentCache,
  getDesciplineResponse,
  getUpdateProfileAffiliations,
  isSegUser,
  selectUniqueEmailStatusIsLoading,
  selectUser,
} from '../../user-profile/user-profile.selectors';
import { getSelectedCountry } from '../../country-selector/country';
import './school-selector';
import './department-selector';
import { initUpdateProfileWithAffiliation, pushAffiliations, resetAffiliations, resetSchoolResponse, updateAffiliations } from '../../user-profile';
import { countryHasStates, getSelectedCountryState } from '../../country-selector/country-state';
import { isHEInstructor } from '../../../common/utils/cart-util';
import { MyInformationFormAnalytics } from './my-information-form.analytics';
import { fetchDescipline } from '../../user-profile/thunks/fetch-descipline.thunk';
import { DepartmentSelect } from './department-selector';
import { SchoolSelect } from './school-selector';
import { fetchDepartments } from '../../user-profile/thunks/fetch-departments.thunk';
import { ScrollBehavior } from '../../base-component/browser';
import { wait } from '../../../common/utils/util';


enum USER_FLOW {
  HE_STUDENT = 'HE_STUDENT',
  HE_INSTRUCTOR = 'HE_INSTRUCTOR',
  SEG_USER = 'SEG_USER',
}

@customElement('mhecomm-my-information-form')
export class MyInformationForm extends connect(store)(MyAccountFormWrapper) {
  static styles = [
    css`
      .spinner-container {
        display: none;
      }

      input.loading + .spinner-container {
        display: block;
      }
    `,
  ];

  @query('mhecomm-country-state-selector')
  protected stateSelector: HTMLFormElement | undefined;

  @query('mhecomm-school-selector')
  protected schoolSelector: HTMLFormElement | undefined;

  @query('mhecomm-department-selector')
  protected departmentSelector: HTMLFormElement | undefined;

  @state()
  private user: UserProfile | undefined;

  @state()
  private emailValidationApiIsLoading;

  @state()
  private affiliations: UserAffiliation[] = [];

  @state()
  private showSchoolForm = false;

  @state()
  private selectedCountry = 'US';

  @state()
  private selectedState: any;

  @state()
  private showDepartmentField = false;

  @state()
  private desciplineList: any[] = [];

  @state()
  private departmentCache: Record<string, { id: any; name: string }[]> = {};

  @state()
  private a11yAnnounceAddedSchool = '';

  @state()
  private formFlow = USER_FLOW.HE_STUDENT;

  @state()
  private feedbackMessage = '';

  @state()
  private editingDepartmentIndex: number | null = null;

  @state()
  private duplicateSchoolError = false;

  private editInformationButton = 'my-account-edit-information-button';
  private editMyInformationButtonLabel = 'Edit My Information';

  private updateUserInfoButtonId = 'my-account-update-user-information-button';
  private updateUserInfoButtonText = 'Save Changes';

  private removeSchoolButtonId = 'my-account-remove-school-button';
  private removeSchoolButtonText = 'Remove';

  private addSchoolButtonId = 'my-account-add-school-button';
  private addSchoolButtonText = 'Add School';

  private openAddSchoolFormButtonId = 'my-account-open-add-school-form-button';

  private firstNameId = `givenName`;
  private lastNameId = `familyName`;
  private emailId = `email`;
  private country = `country`;
  private state = `state`;
  private school = `school`;
  private department = `department`;
  private affiliationToAdd: UserAffiliation | null = null;

  private countryStoreKey = 'userProfileCountryKey';
  private stateStoreKey = 'userProfileStateKey';
  private schoolStoreKey = 'userProfileSchoolKey';
  private departmentStoreKey = 'userProfileDepartmentKey';

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): any {
    super.firstUpdated(_changedProperties);
    store.dispatch(fetchDescipline());
  }

  protected validationMessage() {
    return {
      [this.firstNameId]: 'Please enter your first name',
      [this.lastNameId]: 'Please enter your last name.',
      [this.emailId]: 'Use format name@company.com format for your email address.',
    };
  }

  stateChanged(state: StoreState) {
    this.user = selectUser(state);

    if (isHEInstructor(this.user)) {
      this.formFlow = USER_FLOW.HE_INSTRUCTOR;
      this.showDepartmentField = true;
    } else if (isSegUser(this.user)) {
      this.formFlow = USER_FLOW.SEG_USER;
    } else {
      this.formFlow = USER_FLOW.HE_STUDENT;
    }

    this.desciplineList = getDesciplineResponse(state);
    const rawCache = getDepartmentCache(state);
    this.departmentCache = Object.fromEntries(
      Object.entries(rawCache).map(([cc, s]) => [cc, (s as any).data || []])
    );

    this.selectedCountry = getSelectedCountry(this.countryStoreKey, state) || 'US';
    this.selectedState = getSelectedCountryState(this.stateStoreKey, state);
    this.emailValidationApiIsLoading = selectUniqueEmailStatusIsLoading(state);
    this.affiliations = getUpdateProfileAffiliations(state);

    if (this.formFlow === USER_FLOW.HE_STUDENT) {
      this.showSchoolForm = !this.affiliations.length;
    }
  }

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);

    if (_changedProperties.has('selectedCountry')) {
      this.stateSelector?.resetSelectOption();
      this.schoolSelector?.resetSelectOption();
      this.departmentSelector?.resetSelectOption();
    }

    if (_changedProperties.has('selectedState')) {
      this.schoolSelector?.resetSelectOption();
      this.departmentSelector?.resetSelectOption();
    }

    if (_changedProperties.has('selectedCountry') || _changedProperties.has('selectedState')) {
      store.dispatch(resetSchoolResponse());
    }

    if (_changedProperties.has('editMode') && this.editMode) {
      this.scrollFirstFiledIntoView();
    }
  }

  async scrollFirstFiledIntoView() {
    await wait(100);
    const firstNameEl = this.shadowRoot?.querySelector(`#${this.firstNameId}`) as HTMLElement;
    if (firstNameEl && !this.isInViewPort(firstNameEl)) {
      firstNameEl.scrollIntoView({ behavior: ScrollBehavior.smooth });
    }
  }

  renderComp() {
    return html`
      ${this.compState === PassValidationCompStatus.succeeded ? this.renderEditMode() : this.renderReadonlyMode()}
      <div class="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        ${this.feedbackMessage}
      </div>
    `;
  }

  inputFieldAndLabel(id, label, value, type) {
    return html`<label class="form-label" for="${id}">${label}</label>
      <input
        class="form-control"
        id="${id}"
        name="${id}"
        type="${type}"
        value="${value}"
        required
        aria-describedby="${id}-field-error"
      />`;
  }

  renderEditMode() {
    return html`
      <form class="needs-validation align-items-center g-2" @submit=${this.submit} novalidate>
        <div class="row">
          <div class="col-12 col-lg-6">
            <label class="form-label" for="${this.firstNameId}">First Name</label>
            <input
              class="form-control"
              id="${this.firstNameId}"
              name="${this.firstNameId}"
              type="text"
              value="${ifDefined(this.user?.givenName)}"
              required
              aria-describedby="${this.firstNameId}-field-error"
              autofocus
            />
          </div>
        </div>
        <div class="col-12"><hr aria-hidden="true"/></div>
        <div class="row">
          <div class="col-12 col-lg-6">
            ${this.inputFieldAndLabel(this.lastNameId, 'Last Name', this.user?.familyName, 'text')}
          </div>
        </div>
        <div class="col-12"><hr aria-hidden="true"/></div>
        <div class="row">
          <div class="col-12 col-lg-6">
            <label class="form-label" for="${this.emailId}">Email Address</label>
            ${this.isLMSFlow
              ? html` <div id="${this.emailId}">${this.user?.email}</div> `
              : html`
                  <div class="position-relative">
                    <input
                      class="form-control ${this.emailValidationApiIsLoading ? ' loading pe-4' : ''}"
                      id="${this.emailId}"
                      name="${this.emailId}"
                      type="email"
                      value="${ifDefined(this.user?.email)}"
                      required
                      aria-describedby="${this.emailId}-field-error"
                    />
                    <div class="position-absolute top-50 end-0 translate-middle-y me-2 spinner-container">
                      <div class="spinner-border text-gray spinner-border-sm" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  </div>
                `}
          </div>
        </div>
        <hr aria-hidden="true"/>
        ${this.formFlow === USER_FLOW.SEG_USER ? this.renderSEGUserSchoolTemplate() : this.renderHeUserSchoolTemplate()}
        ${this.a11yAnnounceAddedSchool
          ? html`<div class="visually-hidden" aria-live="polite">${this.a11yAnnounceAddedSchool}</div>`
          : nothing}
        ${!this.showSchoolForm
          ? html`<div class="text-end">
              <button
                id="${this.updateUserInfoButtonId}"
                class="btn btn-primary me-2 w-100 w-md-auto mb-2 mb-md-0"
                ?disabled=${this.isSubmitted}
              >
                ${this.isSubmitted ? spinner({ size: 'sm' }) : `${this.updateUserInfoButtonText}`}
              </button>
              <button
                class="btn btn-outline-primary w-100 w-md-auto"
                type="button"
                ?disabled=${this.isSubmitted}
                @click=${() => this.onCancel()}
              >
                Cancel
              </button>
            </div>`
          : nothing}
      </form>
    `;
  }

  renderHeUserSchoolTemplate() {
    return html`
      <div class="row">
        <div class="col-12">
          <div class="row">
            <div class="col">
              <p class="fw-semibold">My Schools</p>
            </div>
            ${this.formFlow === USER_FLOW.HE_INSTRUCTOR && !this.showSchoolForm
              ? html`<div class="col-auto">
                  <button
                    class="btn btn-link"
                    id="${this.openAddSchoolFormButtonId}"
                    @click=${() => {
                      this.showSchoolForm = true;
                      MyInformationFormAnalytics.openAddSchoolForm(this, this.user!);
                    }}>
                    + Add a School
                  </button>
                </div>`
              : nothing}
          </div>

          ${this.showSchoolForm && this.affiliations.length === 0
            ? html`<div
                class="alert alert-danger py-2 fs-6 invalid-feedback d-block"
                role="alert"
                aria-live="polite"
                id="${this.school}-field-error">
                <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon>
                <span class="visually-hidden">Error&nbsp;</span>
                You must have at least one university associated with
                your account.
              </div>`
            : nothing}
          ${this.duplicateSchoolError
            ? html`<div
                class="alert alert-danger py-2 fs-6 invalid-feedback d-block"
                role="alert"
                aria-live="polite"
                id="${this.school}-duplicate-error">
                <uxt-icon icon="exclamation-triangle" class="me-1"></uxt-icon>
                <span class="visually-hidden">Error&nbsp;</span>
                This school has already been added.
              </div>`
            : nothing}
          ${this.showSchoolForm
            ? html`<div class="row g-3 mb-4">
                <div class=${this.showDepartmentField ? 'col-12 col-md-6' : 'col-lg-3 col-md-6'}>
                  <mhecomm-country-selector
                    id=${this.country}
                    type=${this.countryStoreKey}
                    label="Country (Optional)"
                    selectedOption="${this.selectedCountry}"
                    errorMessage="Please select a valid country"
                    ?isdirty=${this.isDirty}
                  >
                  </mhecomm-country-selector>
                </div>
                ${this.showStateField()
                  ? html`<div class=${this.showDepartmentField ? 'col-12 col-md-6' : 'col-lg-3 col-md-6'}>
                      <mhecomm-country-state-selector
                        id=${this.state}
                        type=${this.stateStoreKey}
                        label="State (Optional)"
                        countryCode=${this.selectedCountry}
                        selectedOption=${this.selectedState}
                        errorMessage="Please select a valid state"
                        ?capitalize=${true}
                        ?isdirty=${this.isDirty}
                      >
                      </mhecomm-country-state-selector>
                    </div>`
                  : nothing}
                <div class=${this.showDepartmentField ? 'col-12 col-md-6' : 'col'}>
                  <mhecomm-school-selector
                    id=${this.school}
                    field=${this.schoolStoreKey}
                    .countryCode=${this.selectedCountry}
                    .stateCode=${this.selectedState}
                    errorMessage="Please select a valid school"
                    ?isdirty=${this.isDirty}
                    required="true"
                    @onSchoolSelection=${(e) => this.onSchoolSelection(e.detail)}
                  >
                  </mhecomm-school-selector>
                </div>
                ${this.showDepartmentField
                  ? html`<div class=${this.showDepartmentField ? 'col-12 col-md-6' : 'col'}>
                      <mhecomm-department-selector
                        id=${this.department}
                        type=${this.departmentStoreKey}
                        label="Department"
                        .countryCode=${this.selectedCountry}
                        errorMessage="Please select a valid department"
                        ?isdirty=${this.isDirty}
                        required
                        @onDepartmentSelection=${(e) => this.onDepartmentSelection(e.detail)}
                      >
                      </mhecomm-department-selector>
                    </div>`
                  : nothing}
                <div class="col-lg-auto col-12 ${this.showDepartmentField ? '' : 'pt-lg-2'}">
                  ${!this.showDepartmentField ? html`<br class="d-none d-lg-block" />` : nothing}
                  ${this.affiliations.length > 0
                    ? html`<button
                        class="btn btn-outline-primary w-100 w-md-auto mb-md-0 mb-2"
                        type="button"
                        @click=${() => this.cancelAddSchool()}>
                        Cancel
                      </button>`
                    : nothing}
                  <button
                    id="${this.addSchoolButtonId}"
                    class="btn btn-primary w-100 w-md-auto"
                    data-automation-id="add-school"
                    @click=${() => this.addSchool()}
                    type="button"
                  >
                    ${this.addSchoolButtonText}
                  </button>
                </div>
              </div>`
            : nothing}
          <div class="row g-3 mb-4">
            ${this.affiliations.map(
              (a, index) =>
                html`<div class="col-12 col-md-6 col-lg-3">
                  <div class="rounded-2 p-3 bg-light h-100">
                    ${a.orgName}<br />
                    ${a.orgCity ? html`${a.orgCity}, ` : ''} ${a.orgCountry ? html`${a.orgCountry} <br />` : ''}
                    ${this.formFlow === USER_FLOW.HE_INSTRUCTOR
                      ? this.editingDepartmentIndex === index
                        ? html`<div class="mt-2 mb-2">
                            <mhecomm-department-selector
                              id="edit-department-${index}"
                              type="editDept-${index}"
                              label="Department"
                              .countryCode=${a.orgCountry || 'US'}
                              .selectedOption=${a.department || ''}
                              @onDepartmentSelection=${(e) => this.onEditDepartment(index, e.detail)}
                            >
                            </mhecomm-department-selector>
                            <button
                              class="btn btn-sm btn-outline-primary mt-1"
                              type="button"
                              @click=${() => (this.editingDepartmentIndex = null)}
                            >
                              Cancel
                            </button>
                          </div>`
                        : html`${a.department && this.getDepartmentList(a.department, a.orgCountry || 'US')
                            ? html`${this.getDepartmentList(a.department, a.orgCountry || 'US')}
                                <button
                                  class="btn btn-sm link-primary ps-1"
                                  type="button"
                                  aria-label="Edit department for ${a.orgName}"
                                  @click=${() => this.startEditDepartment(index, true)}
                                >
                                  Edit
                                </button><br />`
                            : html`<button
                                class="btn btn-sm link-primary ps-0"
                                type="button"
                                aria-label="Add department for ${a.orgName}"
                                @click=${() => this.startEditDepartment(index, false)}
                              >
                                + Add Department
                              </button><br />`}`
                      : nothing}
                    <button
                      id="${this.removeSchoolButtonId}"
                      class="btn link-primary ps-0"
                      type="button"
                      aria-label="Remove ${a.orgName}"
                      @click=${() => this.removeAffiliation(index)}
                    >
                      ${this.removeSchoolButtonText}
                    </button>
                  </div>
                </div>`
            )}
          </div>
        </div>
      </div>
    `;
  }

  renderSEGUserSchoolTemplate() {
    return html`
      <div class="row mb-4">
        <div class="col-12">
          <p class="fw-semibold mb-4">My School Information</p>
          ${this.user?.affiliations.map(
            (affiliation, index, affiliations) => html`<div>
                ${affiliation.jobTitle
                  ? html`<span class="fw-semibold mb-4">Job Title: </span> ${affiliation.jobTitle} <br />`
                  : nothing}
                ${affiliation.discipline
                  ? html` <span class="fw-semibold mb-4">Discipline: </span> ${this.getDisciplineName(
                        affiliation.discipline
                      )} <br />`
                  : nothing}
                <span class="fw-semibold mb-4">School: </span>
                ${affiliation.orgName ? html`${affiliation.orgName}, ` : nothing}
                ${affiliation.orgCity ? html`${affiliation.orgCity}, ` : nothing}
                ${affiliation.orgCountry ? html`${affiliation.orgCountry}, ` : nothing}
                ${affiliation.orgState ? affiliation.orgState?.toUpperCase() : nothing}
                <br />
              </div>
              ${index !== (affiliations.length - 1) ? html`<hr aria-hidden="true"/>` : nothing}`
          )}
        </div>
      </div>
    `;
  }

  getDisciplineName(id: string) {
    return this.desciplineList.find((o) => o.id == id)?.name;
  }

  getDepartmentList(id: string, countryCode: string = 'US') {
    return (this.departmentCache[countryCode] || []).find((o) => o.id == id)?.name;
  }

  onCancel() {
    MyInformationFormAnalytics.cancel(this, this.user!);
    this.showSchoolForm = false;
    this.updateComponentStatus(PassValidationCompStatus.default);
  }

  onSchoolSelection(selectedSchool) {
    if (!selectedSchool) {
      return;
    }

    this.duplicateSchoolError = false;
    MyInformationFormAnalytics.schoolSelected(this, selectedSchool);
    this.affiliationToAdd = {
      orgCity: selectedSchool.address.city,
      orgCountry: selectedSchool.address.country,
      orgName: selectedSchool.name,
      orgState: selectedSchool.address.state,
      orgXid: selectedSchool.xid,
      role: this.user?.userType as any,
      segment: this.user?.persona.segment as any,
    };

    if (this.showDepartmentField) {
      const departmentComp = this.shadowRoot?.querySelector('mhecomm-department-selector') as DepartmentSelect;
      if (departmentComp) {
        departmentComp.selectedOption = '';
      }
    }
  }

  startEditDepartment(index: number, hasExistingDepartment: boolean) {
    const affiliation = this.affiliations[index];
    store.dispatch(fetchDepartments({ countryCode: affiliation.orgCountry || 'US' }));
    this.editingDepartmentIndex = index;
    MyInformationFormAnalytics.startEditDepartment(this, this.user!, hasExistingDepartment);
  }

  onEditDepartment(index: number, selectedDepartment: string) {
    const updated = this.affiliations.map((a, i) =>
      i === index ? { ...a, department: selectedDepartment } : a
    );
    store.dispatch(updateAffiliations(updated));
    this.editingDepartmentIndex = null;
    MyInformationFormAnalytics.saveDepartmentEdit(this, this.user!);
  }

  onDepartmentSelection(selectedDepartment) {
    if (this.affiliationToAdd) {
      this.affiliationToAdd.department = selectedDepartment;
      const countryCode = this.affiliationToAdd.orgCountry || 'US';
      const departmentObj = (this.departmentCache[countryCode] || []).find((d) => d.id == selectedDepartment);
      MyInformationFormAnalytics.departmentSelected(this, this.user!, departmentObj, this.affiliationToAdd);
    }
  }

  addSchool() {
    if (!this.affiliationToAdd || (this.showDepartmentField && !this.affiliationToAdd.department)) {
      (this.shadowRoot?.querySelector('mhecomm-school-selector') as SchoolSelect).validateFields();
      (this.shadowRoot?.querySelector('mhecomm-department-selector') as DepartmentSelect).validateFields();
      return;
    }

    // Check if school already exists in affiliations
    const isDuplicate = this.affiliations.some(
      (a) => a.orgXid === this.affiliationToAdd?.orgXid
    );

    if (isDuplicate) {
      this.duplicateSchoolError = true;
      return;
    }

    this.duplicateSchoolError = false;
    MyInformationFormAnalytics.addSchool(this, this.user!, this.addSchoolButtonText);
    store.dispatch(pushAffiliations(this.affiliationToAdd));
    this.a11yAnnounceAddedSchool = this.affiliationToAdd.orgName + ' added';
    this.affiliationToAdd = null;
    this.showSchoolForm = false;
  }

  cancelAddSchool() {
    this.showSchoolForm = false;
    this.affiliationToAdd = null;
    this.duplicateSchoolError = false;
    this.stateSelector?.resetSelectOption();
    this.schoolSelector?.resetSelectOption();
    this.departmentSelector?.resetSelectOption();

    if (this.formFlow === USER_FLOW.HE_STUDENT) {
      store.dispatch(resetAffiliations());
    }
  }

  removeAffiliation(index) {
    if (this.affiliations.length === 1) {
      this.showSchoolForm = true;
    }
    const removedAffiliation = this.affiliations[index];
    const countryCode = removedAffiliation.orgCountry || 'US';
    const removedDepartment = removedAffiliation.department
      ? (this.departmentCache[countryCode] || []).find((d) => d.id == removedAffiliation.department) || { id: removedAffiliation.department }
      : undefined;
    store.dispatch(updateAffiliations(this.affiliations.filter((_, i) => i != index)));
    MyInformationFormAnalytics.removeSchool(this, this.user!, this.removeSchoolButtonText, removedAffiliation, removedDepartment);
  }

  private showStateField() {
    return countryHasStates(this.selectedCountry.toUpperCase());
  }

  protected customValidator() {
    return {
      [this.emailId]: async (inputElement: HTMLInputElement) => {
        if (!this.user?.email || !inputElement.value || inputElement.value === this.user.email) {
          return false;
        }

        try {
          const res: any = await store.dispatch(validateUniqueEmail({ email: inputElement.value }));
          if (res.error) {
            return false;
          }
          return `You already have an account with that email address. Please <a href="${
            environment().loginUrl
          }">sign in</a>.`;
        } catch {
          return false;
        }
      },
    };
  }

  renderReadonlyMode() {
    return html`
      <dl id="user-info-details" class="row mb-0">
        <div class="col-12 border-bottom pb-3 mb-3">
          <dt id="user-first-name-label" class="fw-semibold">First Name</dt>
          <dd id="user-first-name-value" class="fw-light">${this.user?.givenName}</dd>
        </div>
        <div class="col-12 border-bottom pb-3 mb-3">
          <dt id="user-last-name-label" class="fw-semibold">Last Name</dt>
          <dd id="user-last-name-value" class="fw-light">${this.user?.familyName}</dd>
        </div>
        <div class="col-12 border-bottom pb-3 mb-3">
          <dt id="user-email-label" class="fw-semibold">Email Address</dt>
          <dd id="user-email-value" class="fw-light">${this.user?.email}</dd>
        </div>
        ${this.formFlow === USER_FLOW.SEG_USER
          ? this.renderSEGUserSchoolTemplate()
          : html`
              <div class="col-12">
                <dt id="user-schools-label" class="fw-semibold">My Schools</dt>
                <dd id="user-schools-value">
                  <ul class="row mb-4 g-3 list-unstyled">
                    ${this.user?.affiliations.map(
                      (a) => html`<li class="col-12 col-md-6 col-lg-3">
                        <div class="rounded-2 p-3 bg-light h-100">
                          ${a.orgName}<br />
                          ${a.orgCity ? html`${a.orgCity},` : nothing}
                          ${a.orgCountry ? html`${a.orgCountry}<br />` : nothing}
                          ${this.formFlow === USER_FLOW.HE_INSTRUCTOR &&
                          a.department &&
                          this.getDepartmentList(a.department)
                            ? html`${this.getDepartmentList(a.department)}<br />`
                            : nothing}
                        </div>
                      </li>`
                    )}
                  </ul>
                </dd>
              </div>
            `}
      </dl>
      ${this.isDefault()
        ? html`<div class="row">
            <div class="col-12 text-center text-md-end">
              <button
                id="${this.editInformationButton}"
                class="btn btn-link fw-bold"
                data-automation-id="edit-my-information"
                @click=${() => this.onClickEditMyInformationButton()}
              >
                ${this.editMyInformationButtonLabel}
              </button>
            </div>
          </div>`
        : nothing}
    `;
  }

  onClickEditMyInformationButton() {
    this.feedbackMessage = '';
    // Pre-fetch departments for all countries present in existing affiliations
    const countryCodes = [...new Set(
      (this.user?.affiliations || []).map(a => a.orgCountry).filter(Boolean)
    )] as string[];
    countryCodes.forEach(cc => store.dispatch(fetchDepartments({ countryCode: cc })));
    if(this.isLMSFlow) {
      this.updateComponentStatus(PassValidationCompStatus.succeeded);
      store.dispatch(initUpdateProfileWithAffiliation());
    } else {
      this.updateComponentStatus(PassValidationCompStatus.waiting);
    }
    MyInformationFormAnalytics.editMyInformation(this, this.user!, this.editMyInformationButtonLabel);
  }

  showFeedbackMessage(message: string) {
    this.feedbackMessage = message;
  }

  async submit(e) {
    super.submit(e);
    if (this.isSubmitted) {
      return;
    }

    // Validate that at least one affiliation exists
    if (this.affiliations.length === 0) {
      this.showFeedbackMessage('You must have at least one school associated with your account.');
      return;
    }

    if (await this.isFormValid()) {
      this.isSubmitted = true;
      const userData: any = this.getRawValue();

      if (this.isLMSFlow) {
        userData.isLMSFlow = this.isLMSFlow;
      }

      try {
        await store.dispatch(updateUserProfile(userData)).unwrap();
        this.showFeedbackMessage('Changes saved successfully.');
        MyInformationFormAnalytics.saveChanges(this, this.user!, this.updateUserInfoButtonText);
      } catch (e) {
        console.error(e);
        this.showFeedbackMessage('Failed to save changes. Please try again.');
      } finally {
        this.isSubmitted = false;
      }
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-my-information-form': MyInformationForm;
  }
}